import * as SelectPrimitive from "@radix-ui/react-select";
import { ChevronDown } from "lucide-react";
import * as React from "react";

import { cn } from "../../../utils";
import { PopoverTrigger } from "../../Popover";
import { SelectContext } from "./context";

export const SelectTrigger = ({
  className,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger>) => {
  const ctx = React.useContext(SelectContext);

  // -------------------------------------------------------------------------
  // Simple Radix branch
  // -------------------------------------------------------------------------
  // typography: select-trigger primitive — defines own button visual contract (font-semibold). Placeholder uses gray-500 per TYPOGRAPHY.md §2.
  if (ctx.isSimple) {
    return (
      <SelectPrimitive.Trigger
        ref={ref}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-sm border border-gray-200 bg-white p-2 px-4 text-sm shadow-md placeholder:text-gray-500 placeholder:font-normal focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 font-semibold hover:border-primary/30",
          className,
        )}
        {...props}
      >
        {children}
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="size-4 opacity-50" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
    );
  }

  // -------------------------------------------------------------------------
  // Enhanced branch — custom button + PopoverTrigger
  // -------------------------------------------------------------------------
  const {
    isMobile,
    open,
    handleOpenChange,
    multiple,
    disabled: ctxDisabled,
  } = ctx;
  const {
    asChild: _asChild,
    disabled: triggerDisabled,
    onClick,
    ...buttonProps
  } = props as React.ComponentPropsWithoutRef<"button"> & {
    asChild?: boolean;
  };
  const isDisabled = triggerDisabled ?? ctxDisabled;

  const button = (
    <button
      ref={ref}
      type="button"
      disabled={isDisabled}
      onClick={(e) => {
        onClick?.(e);
        if (!e.defaultPrevented && isMobile) {
          handleOpenChange(!open);
        }
      }}
      aria-expanded={open}
      aria-haspopup="listbox"
      {...buttonProps}
      className={cn(
        "flex w-full items-center justify-between rounded-sm border border-gray-200 bg-white px-4 text-sm shadow-md placeholder:text-gray-500 placeholder:font-normal focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 font-semibold hover:border-primary/30",
        multiple ? "min-h-10 py-1.5" : "h-10 py-2",
        open && "ring-1 ring-primary",
        className,
      )}
    >
      <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 text-left">
        {children}
      </span>
      <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
    </button>
  );

  if (isMobile) {
    return button;
  }

  return <PopoverTrigger asChild>{button}</PopoverTrigger>;
};

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
